import { requestFileSignature } from 'graphql/documents/mutations';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import useAlert from 'utils/alert';

import { RequestFileSignatureForm } from './types';

type UseRequestSignatureParams = {
  documentId: string;
  fileSource: string;
  onClose: () => void;
};

export const useRequestSignature = ({
  documentId,
  fileSource,
  onClose,
}: UseRequestSignatureParams) => {
  const { t } = useTranslation();
  const Alert = useAlert();
  const { id: orderId } = useParams<{ id: string }>();
  const [errors, setErrors] = useState<null | string>(null);
  const {
    handleSubmit,
    control,
    getValues,
    formState: { isValid: isFormValid },
  } = useForm<RequestFileSignatureForm>({
    defaultValues: {
      embedded: false,
    },
    mode: 'onChange',
  });

  const queryCache = useQueryClient();

  const { mutate: requestSignature, isLoading: isRequestSignatureLoading } = useMutation(
    requestFileSignature,
    {
      onSuccess: () => {
        setErrors(null);
        Alert(t('ron:document-signature-request-send'), 'success');
        queryCache.invalidateQueries(['getSignatures', orderId]);
        onClose();
      },
      onError: (e: { response: { errors: { message: string }[] } }) => {
        const errorMessage =
          e.response && e.response.errors && e.response.errors.length > 0
            ? e.response.errors[0].message
            : t('documents:lack-of-email');
        setErrors(errorMessage);
        Alert(errorMessage, 'error');
      },
    }
  );

  const onSendRequest = handleSubmit((requestInfo) => {
    requestSignature({
      orderId,
      embedded: requestInfo.embedded,
      source: fileSource,
      fileId: documentId,
    });
  });

  return {
    t,
    isRequestSignatureLoading,
    isFormValid,
    control,
    getValues,
    onSendRequest,
    errors,
    setErrors,
  };
};
